import { useEffect, useState } from "react";
import { Form, Button, Row, Col, Card, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import useAxiosPrivate from "../../../Pages/hooks/useAxiosPrivate";
import {
  ACADEMICYEARID,
  ACADIMAC,
  ADDEXAM,
  ADDQUESTIONS,
  baseURL,
} from "../../../API/API";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const EMPTY_MCQ_CHOICES = { 1: "", 2: "", 3: "", 4: "" };

function createEmptyQuestion() {
  return {
    question: "",
    questionType: "mcq",
    grade: "",
    selectedOption: null,
    mcqChoices: { ...EMPTY_MCQ_CHOICES },
    tfAnswer: "",
    modelAnswer: "",
  };
}

function buildQuestionPayload(question, examMeta) {
  const base = {
    ...examMeta,
    question: question.question.trim(),
    grade: Number(question.grade),
    save: false,
  };

  if (question.questionType === "mcq") {
    const optionsText = {};
    [1, 2, 3, 4].forEach((key) => {
      const text = (question.mcqChoices[key] || "").trim();
      if (text) {
        optionsText[key] = text;
      }
    });

    return {
      ...base,
      questionType: "mcq",
      optionsText,
      selectedOption: question.selectedOption,
      answare: optionsText[question.selectedOption] || "",
    };
  }

  if (question.questionType === "truefalse") {
    const selectedOption = question.tfAnswer === "صح" ? 1 : 2;

    return {
      ...base,
      questionType: "truefalse",
      selectedOption,
      answare: question.tfAnswer,
      optionsText: ["صح", "خطأ"],
    };
  }

  return {
    ...base,
    questionType: "text",
    optionsText: [],
    answare: question.modelAnswer.trim(),
    modelAnswer: question.modelAnswer.trim(),
  };
}

export default function CreateFullExam() {
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [academics, setAcademics] = useState([]);
  const [chapters, setChapters] = useState([]);
  const [examData, setExamData] = useState({
    examName: "",
    academicYear: "",
    chapter: "",
    time: "",
  });
  const [questions, setQuestions] = useState([createEmptyQuestion()]);

  useEffect(() => {
    async function loadAcademics() {
      try {
        const res = await axiosPrivate.get(`${baseURL}/${ACADIMAC}`);
        setAcademics(res.data.data || []);
      } catch (err) {
        console.error("Error loading academic years:", err);
      }
    }

    loadAcademics();
  }, [axiosPrivate]);

  useEffect(() => {
    async function loadChapters() {
      if (!examData.academicYear) {
        setChapters([]);
        return;
      }

      try {
        const res = await axiosPrivate.get(
          `${baseURL}/${ACADEMICYEARID}/${examData.academicYear}`
        );
        setChapters(res.data.data || []);
      } catch (err) {
        console.error("Error loading chapters:", err);
        setChapters([]);
      }
    }

    loadChapters();
  }, [examData.academicYear, axiosPrivate]);

  function updateExamField(name, value) {
    setExamData((prev) => ({ ...prev, [name]: value }));
  }

  function updateQuestion(index, patch) {
    setQuestions((prev) =>
      prev.map((item, idx) => (idx === index ? { ...item, ...patch } : item))
    );
  }

  function handleQuestionTypeChange(index, questionType) {
    updateQuestion(index, {
      questionType,
      selectedOption: null,
      mcqChoices: { ...EMPTY_MCQ_CHOICES },
      tfAnswer: "",
      modelAnswer: "",
    });
  }

  function addQuestionRow() {
    if (questions.length >= 50) return;
    setQuestions((prev) => [...prev, createEmptyQuestion()]);
  }

  function removeQuestionRow(index) {
    if (questions.length <= 1) return;
    setQuestions((prev) => prev.filter((_, idx) => idx !== index));
  }

  function validateForm() {
    if (!examData.examName.trim()) {
      toast.error("ادخل اسم الامتحان");
      return false;
    }
    if (!examData.academicYear) {
      toast.error("اختر السنة الدراسية");
      return false;
    }
    if (!examData.chapter) {
      toast.error("اختر الشهر الدراسي");
      return false;
    }
    if (!examData.time) {
      toast.error("ادخل وقت الامتحان");
      return false;
    }

    for (let i = 0; i < questions.length; i += 1) {
      const q = questions[i];
      const row = i + 1;

      if (!q.question.trim()) {
        toast.error(`ادخل نص السؤال في الصف ${row}`);
        return false;
      }
      if (!q.grade || Number(q.grade) <= 0) {
        toast.error(`ادخل درجة صحيحة في الصف ${row}`);
        return false;
      }

      if (q.questionType === "mcq") {
        const filled = [1, 2, 3, 4].filter((k) => (q.mcqChoices[k] || "").trim());
        if (filled.length < 2) {
          toast.error(`ادخل على الأقل اختيارين في الصف ${row}`);
          return false;
        }
        if (!q.selectedOption) {
          toast.error(`حدد الإجابة الصحيحة (زر الاختيار) في الصف ${row}`);
          return false;
        }
        if (!(q.mcqChoices[q.selectedOption] || "").trim()) {
          toast.error(`الإجابة المحددة فارغة في الصف ${row}`);
          return false;
        }
      }

      if (q.questionType === "truefalse" && !q.tfAnswer) {
        toast.error(`حدد الإجابة الصحيحة (صح أو خطأ) في الصف ${row}`);
        return false;
      }

      if (q.questionType === "text" && !q.modelAnswer.trim()) {
        toast.error(`ادخل الإجابة النموذجية للسؤال المقالي في الصف ${row}`);
        return false;
      }
    }

    return true;
  }

  async function handleCreateFullExam() {
    if (!validateForm()) return;
    setLoading(true);

    try {
      const examRes = await axiosPrivate.post(`${baseURL}/${ADDEXAM}/`, {
        academicYear: examData.academicYear,
        chapter: examData.chapter,
        examName: examData.examName,
        time: examData.time,
        save: false,
      });

      const examId = examRes.data?.id;
      if (!examId) {
        throw new Error("No exam id returned");
      }

      const examMeta = {
        academicYear: examData.academicYear,
        chapter: examData.chapter,
        exam: examId,
      };

      for (const question of questions) {
        await axiosPrivate.post(
          `${baseURL}/${ADDQUESTIONS}/`,
          buildQuestionPayload(question, examMeta)
        );
      }

      toast.success("تم إنشاء الامتحان مع الأسئلة بنجاح");
      navigate("/DashboardLayout/tests/create-test");
    } catch (err) {
      console.error("Error creating full exam:", err);
      toast.error("حدث خطأ أثناء إنشاء الامتحان، حاول مرة أخرى");
    } finally {
      setLoading(false);
    }
  }

  function renderMcqBlock(row, index) {
    return (
      <Col xs={12}>
        <Form.Label className="fw-semibold">الاختيارات — حدد الإجابة الصحيحة</Form.Label>
        {[1, 2, 3, 4].map((optionKey) => (
          <div
            key={optionKey}
            className="d-flex align-items-center border rounded p-2 mb-2"
            style={{ gap: "12px", cursor: "pointer" }}
            onClick={() => updateQuestion(index, { selectedOption: optionKey })}
          >
            <Form.Check
              type="radio"
              name={`mcq-answer-${index}`}
              checked={row.selectedOption === optionKey}
              readOnly
              onChange={() => updateQuestion(index, { selectedOption: optionKey })}
            />
            <Form.Control
              type="text"
              placeholder={`نص الاختيار ${optionKey}`}
              value={row.mcqChoices[optionKey] || ""}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                updateQuestion(index, {
                  mcqChoices: {
                    ...row.mcqChoices,
                    [optionKey]: e.target.value,
                  },
                })
              }
            />
          </div>
        ))}
      </Col>
    );
  }

  function renderTrueFalseBlock(row, index) {
    return (
      <Col xs={12}>
        <Form.Label className="fw-semibold">الإجابة الصحيحة</Form.Label>
        <div className="d-flex gap-3 flex-wrap">
          {["صح", "خطأ"].map((label) => {
            const isSelected = row.tfAnswer === label;
            return (
              <button
                key={label}
                type="button"
                className={`btn ${isSelected ? "btn-primary" : "btn-outline-secondary"}`}
                style={{ minWidth: "120px", fontWeight: 600 }}
                onClick={() => updateQuestion(index, { tfAnswer: label })}
              >
                {label}
              </button>
            );
          })}
        </div>
      </Col>
    );
  }

  function renderEssayBlock(row, index) {
    return (
      <Col xs={12}>
        <Form.Group>
          <Form.Label className="fw-semibold">الإجابة النموذجية (مرجع للتصحيح اليدوي)</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={row.modelAnswer}
            onChange={(e) => updateQuestion(index, { modelAnswer: e.target.value })}
            placeholder="اكتب الإجابة النموذجية التي يراجعها المعلم"
          />
          <Form.Text className="text-muted">
            الأسئلة المقالية لا تُصحَّح تلقائياً عند تقديم الامتحان.
          </Form.Text>
        </Form.Group>
      </Col>
    );
  }

  return (
    <div className="p-4 content" dir="rtl">
      <ToastContainer position="top-center" reverseOrder={false} />
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold">إنشاء امتحان كامل</h2>
          <p className="text-muted">أضف بيانات الامتحان ثم أضف الأسئلة كاملة.</p>
        </div>
        <Link to="/DashboardLayout/tests/create-test" className="btn btn-outline-secondary">
          العودة لقائمة الاختبارات
        </Link>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <h5 className="mb-3">بيانات الامتحان</h5>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label>اسم الامتحان</Form.Label>
                <Form.Control
                  type="text"
                  value={examData.examName}
                  onChange={(e) => updateExamField("examName", e.target.value)}
                  placeholder="ادخل اسم الامتحان"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>الوقت بالدقائق</Form.Label>
                <Form.Control
                  type="number"
                  value={examData.time}
                  onChange={(e) => updateExamField("time", e.target.value)}
                  placeholder="ادخل وقت الامتحان"
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>السنة الدراسية</Form.Label>
                <Form.Select
                  value={examData.academicYear}
                  onChange={(e) => updateExamField("academicYear", e.target.value)}
                >
                  <option value="">اختر السنة الدراسية</option>
                  {academics.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>الشهر الدراسي</Form.Label>
                <Form.Select
                  value={examData.chapter}
                  onChange={(e) => updateExamField("chapter", e.target.value)}
                  disabled={!chapters.length}
                >
                  <option value="">اختر الشهر الدراسي</option>
                  {chapters.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5>أسئلة الامتحان</h5>
              <p className="text-muted mb-0">أضف حتى 50 سؤالاً في الامتحان.</p>
            </div>
            <Button
              variant="outline-primary"
              onClick={addQuestionRow}
              disabled={questions.length >= 50}
            >
              أضف سؤال
            </Button>
          </div>

          {questions.map((row, index) => (
            <Card key={index} className="mb-3">
              <Card.Body>
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <h6 className="mb-0">سؤال {index + 1}</h6>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => removeQuestionRow(index)}
                    disabled={questions.length <= 1}
                  >
                    احذف
                  </Button>
                </div>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>نص السؤال</Form.Label>
                      <Form.Control
                        type="text"
                        value={row.question}
                        onChange={(e) =>
                          updateQuestion(index, { question: e.target.value })
                        }
                        placeholder="ادخل نص السؤال"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>نوع السؤال</Form.Label>
                      <Form.Select
                        value={row.questionType}
                        onChange={(e) =>
                          handleQuestionTypeChange(index, e.target.value)
                        }
                      >
                        <option value="mcq">اختيار متعدد</option>
                        <option value="truefalse">صح وخطأ</option>
                        <option value="text">مقالي</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group>
                      <Form.Label>الدرجة</Form.Label>
                      <Form.Control
                        type="number"
                        min={1}
                        value={row.grade}
                        onChange={(e) => updateQuestion(index, { grade: e.target.value })}
                        placeholder="الدرجة"
                      />
                    </Form.Group>
                  </Col>

                  {row.questionType === "mcq" && renderMcqBlock(row, index)}
                  {row.questionType === "truefalse" && renderTrueFalseBlock(row, index)}
                  {row.questionType === "text" && renderEssayBlock(row, index)}
                </Row>
              </Card.Body>
            </Card>
          ))}
        </Card.Body>
      </Card>

      <div className="d-flex gap-3">
        <Button variant="success" onClick={handleCreateFullExam} disabled={loading}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" /> جارٍ الإنشاء...
            </>
          ) : (
            "إنشاء الامتحان"
          )}
        </Button>
        <Button
          variant="secondary"
          onClick={() => navigate("/DashboardLayout/tests/create-test")}
        >
          رجوع
        </Button>
      </div>
    </div>
  );
}
